import React, {FC} from 'react';
import styled from 'styled-components';

import {useLocalize} from '../../../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {
  defineMessage,
  Trans,
} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {findThemedImageInfo, ThemedImageNamesEnum} from '../../../../../helpers/imageThemeHelpers';

/*
 * Style.
 */

const StyledEmptyStateWrapperDiv = styled.div`
  display: grid;
  flex: 1;
  padding: 1px 48px 48px 48px;
`;

const StyledEmptyStateCenterDiv = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 6px;
`;

const StyledInfoWrapperDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 310px;
  gap: 24px;
`;

const StyledInfoImg = styled.img<{$imageWidth: number; $imageHeight: number}>`
  width: ${(p) => p.$imageWidth}px;
  height: ${(p) => p.$imageHeight}px;
`;

const StyledInfoTextWrapperDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const StyledInfoHeaderDiv = styled.div`
  color: ${(p) => p.theme.greys.shade90};
  font-size: ${fontSizes.large};
  font-weight: ${fontWeights.semibold};
  line-height: 20px;
  max-width: 480px;
  white-space: normal;
  text-align: center;
`;

const StyledInfoBodyDiv = styled.div`
  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.normal};
  text-align: center;
  line-height: 21px;
`;

/*
 * Component.
 */

export const KnowledgeBaseReportSidePanelErrorState: FC = () => {
  const theme = useUserTheme();
  const localize = useLocalize();
  const searchNoResultsAnywhere = findThemedImageInfo(theme, ThemedImageNamesEnum.SEARCH_NO_RESULTS_ANYWHERE);

  return (
    <StyledEmptyStateWrapperDiv>
      <StyledEmptyStateCenterDiv>
        <StyledInfoWrapperDiv>
          <StyledInfoImg
            $imageWidth={searchNoResultsAnywhere.width}
            $imageHeight={searchNoResultsAnywhere.height}
            src={searchNoResultsAnywhere.src}
            alt={localize(
              defineMessage({
                id: 'analytics-knowledge-base-sidepanel-error-state-image-alt',
                message: 'Oops, something went wrong!',
              }),
            )}
          />
          <StyledInfoTextWrapperDiv>
            <StyledInfoHeaderDiv>
              <Trans id="analytics-knowledge-base-sidepanel-error-state-title">
                Page can&apos;t be found
              </Trans>
            </StyledInfoHeaderDiv>
            <StyledInfoBodyDiv>
              <Trans id="analytics-knowledge-base-sidepanel-error-state-description">
                The page might have been removed or deleted.
              </Trans>
            </StyledInfoBodyDiv>
          </StyledInfoTextWrapperDiv>
        </StyledInfoWrapperDiv>
      </StyledEmptyStateCenterDiv>
    </StyledEmptyStateWrapperDiv>
  );
};
